<template>
  <div>
    <Breadcrumbs :items="breadcrumbs" page_title="live" />

    <v-container fluid class="live-dashboard px-4 px-md-6 py-6">
      <v-row align="center" class="mb-6">
      <v-col cols="12" class="d-flex flex-wrap align-center">
        <v-btn
          outlined
          color="grey darken-2"
          class="mr-3 mb-2 mb-sm-0"
          @click="toggleCalendar"
        >
          <v-icon left>mdi-calendar</v-icon>
          {{ $t("livefeature_calendar") }}
        </v-btn>

        <v-btn
          dark
          class="mb-2 mb-sm-0"
          style="background-color: rgb(166, 204, 57); border-color: rgb(166, 204, 57);"
          @click="openScheduleForm"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t("livefeature_schedule_live_session") }}
        </v-btn>
      </v-col>
    </v-row>

      <v-expand-transition>
        <v-row v-if="showCalendar" class="mb-6">
          <v-col cols="12">
            <v-card flat class="calendar-panel rounded-xl pa-4 pa-md-6">
              <div class="calendar-panel__header mb-4">
                <div>
                  <div class="calendar-panel__title">{{ $t("livefeature_calendar") }}</div>
                  <div class="calendar-panel__subtitle">
                    {{ $t("livefeature_calendar_subtitle") }}
                  </div>
                </div>
              </div>

              <v-sheet class="calendar-panel__sheet">
                <FullCalendar
                  ref="fullCalendar"
                  :options="calendarOptions"
                  :weekends="true"
                  :editable="false"
                  :droppable="false"
                />
              </v-sheet>
            </v-card>
          </v-col>
        </v-row>
      </v-expand-transition>

      <v-row class="mb-4">
      <v-col
        v-for="metric in metrics"
        :key="metric.key"
        cols="12"
        sm="6"
        md="3"
      >
        <v-card flat class="metric-card rounded-xl">
          <div class="metric-label">{{ metric.label }}</div>
          <div class="metric-value" :class="metric.valueClass">
            {{ metric.value }}
          </div>
        </v-card>
      </v-col>
    </v-row>

      <v-row class="mb-6">
      <v-col cols="12">
        <v-tabs
          v-model="activeTab"
          background-color="transparent"
          color="#10b981"
          slider-color="#10b981"
          class="live-tabs"
        >
          <v-tab v-for="tab in tabs" :key="tab.key">
            <v-icon
              v-if="tab.key === 'live'"
              small
              left
              color="red"
            >mdi-circle</v-icon>
            <v-icon
              v-else-if="tab.key === 'scheduled'"
              small
              left
              color="#1976D2"
            >mdi-alarm-check</v-icon>
            <v-icon
              v-else-if="tab.key === 'ended'"
              small
              left
              color="success"
            >mdi-checkbox-marked-circle</v-icon>
            <v-icon
              v-else-if="tab.key === 'recordings'"
              small
              left
              color="brown"
            >mdi-package-variant-closed</v-icon>
            {{ tab.label }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

      <v-row>
      <v-col cols="12">
        <v-alert v-if="sessionsError" type="error" dense outlined class="mb-4">
          {{ sessionsError }}
        </v-alert>

        <v-card
          v-if="sessionsLoading"
          flat
          class="empty-state rounded-xl text-center py-10"
        >
          <v-progress-circular indeterminate color="#10b981" size="28" width="3" />
          <div class="empty-state__subtitle mt-4">{{ $t("livefeature_loading_live_sessions") }}</div>
        </v-card>

        <div
          v-else
          v-for="session in filteredSessions"
          :key="session.id"
          class="session-feed-item mb-4"
        >
          <v-card flat class="session-card rounded-xl">
            <div
              class="session-card__date"
              :class="session.dateBadgeClass"
            >
              <div class="session-card__day">{{ session.dateLabel }}</div>
              <div class="session-card__date-number">{{ session.dateNumber }}</div>
              <div class="session-card__time">{{ session.time }}</div>
            </div>

            <div class="session-card__body">
              <div class="session-card__topline">
                <v-chip
                  small
                  label
                  :class="['session-status-chip', session.statusChipClass]"
                  class="text-uppercase mr-3"
                >
                  <v-icon
                    v-if="session.status === 'scheduled'"
                    small
                    left
                    class="session-status-chip__icon"
                  >
                    mdi-calendar-clock
                  </v-icon>
                  {{ session.statusLabel }}
                </v-chip>
                <span class="session-card__category">{{ session.category }}</span>
              </div>

              <h3 class="session-card__title mb-2">{{ session.title }}</h3>

              <div class="session-card__host mb-3">
                {{ $t("livefeature_host_label") }}: {{ session.host }}
              </div>

              <div class="session-card__meta">
                <span>{{ session.watchersText }}</span>
                <span>{{ session.registrationText }}</span>
                <span>{{ session.duration }}</span>
              </div>
            </div>

            <div class="session-card__actions">
              <v-btn
                v-if="session.normalizedStatus === 'live' && session.canEnterHostRoom && session.isCurrentUserHostOrCoHost"
                color="#ef4444"
                dark
                large
                class="session-action-btn mr-2"
                @click="openHostRoom(session)"
              >
                <v-icon left>mdi-video</v-icon>
                {{ $t("livefeature_enter_host_room") }}
              </v-btn>

              <v-btn
                v-if="session.normalizedStatus === 'live'"
                outlined
                color="#64748b"
                large
                class="session-action-btn"
                @click="openParticipantList(session)"
              >
                <v-icon left>mdi-account-group-outline</v-icon>
                {{ $t("livefeature_view_participant") }}
              </v-btn>

              <template v-else-if="session.normalizedStatus === 'scheduled'">
                <v-btn
                  v-if="session.canEnterHostRoom && session.isCurrentUserHostOrCoHost"
                  color="#ef4444"
                  dark
                  large
                  class="session-action-btn mr-2"
                  @click="openHostRoom(session)"
                >
                  <v-icon left>mdi-video</v-icon>
                  {{ $t("livefeature_enter_host_room") }}
                </v-btn>

                <v-btn
                  outlined
                  color="#64748b"
                  large
                  class="session-action-btn mr-2"
                  @click="openEditForm(session)"
                >
                  <v-icon left>mdi-pencil</v-icon>
                  {{ $t("edit") }}
                </v-btn>

                <v-btn
                  outlined
                  color="#64748b"
                  large
                  class="session-action-btn mr-2"
                  @click="openParticipantList(session)"
                >
                  <v-icon left>mdi-account-group-outline</v-icon>
                  {{ $t("livefeature_view_participant") }}
                </v-btn>

                <v-btn
                  outlined
                  color="#dc2626"
                  large
                  class="session-action-btn"
                  @click="deleteSession(session)"
                >
                  <v-icon left>mdi-delete</v-icon>
                  {{ $t("delete") }}
                </v-btn>
              </template>

              <template v-else-if="session.normalizedStatus === 'ended'">
                <v-btn
                  outlined
                  color="#475569"
                  large
                  class="session-action-btn mr-2"
                  @click="openReport(session)"
                >
                  <v-icon left>mdi-chart-bar</v-icon>
                  {{ $t("livefeature_report") }}
                </v-btn>

                <v-btn
                  outlined
                  color="#64748b"
                  large
                  class="session-action-btn mr-2"
                  @click="openParticipantList(session)"
                >
                  <v-icon left>mdi-account-group-outline</v-icon>
                  {{ $t("livefeature_view_participant") }}
                </v-btn>

                <v-btn
                  outlined
                  color="#dc2626"
                  large
                  class="session-action-btn"
                  @click="deleteSession(session)"
                >
                  <v-icon left>mdi-delete</v-icon>
                  {{ $t("delete") }}
                </v-btn>
              </template>
            </div>
          </v-card>
        </div>

        <v-card
          v-if="!filteredSessions.length"
          flat
          class="empty-state rounded-xl text-center py-10"
        >
          <div class="empty-state__title">{{ $t("livefeature_no_sessions_filter") }}</div>
          <div class="empty-state__subtitle">{{ $t("livefeature_try_another_tab") }}</div>
        </v-card>
      </v-col>
      </v-row>
    </v-container>

    <DeleteConfirmDialog
      :show="deleteDialog"
      :text="deleteConfirmText"
      @close="closeDeleteDialog"
      @confirm="confirmDeleteSession"
    />

    <PermissionDialog
      :show="permissionDialog"
      title="Permission Denied"
      :text="permissionMessage"
      @close="permissionDialog = false"
    />

    <!-- B03 (F3/D27) — หลัง End ไลฟ์ที่มีการบันทึก: แจ้งว่าวิดีโอจะเข้าไลบรารี่ · testid อยู่ใน slot เท่านั้น (E60)
         B03 — วางมุมขวาบน (ไม่ใช่ขวาล่าง): App.vue ใส่ transform บน <v-main> → position: fixed อิงกล่อง v-main ที่สูงทั้งหน้า
         "ล่าง" จึงไปอยู่ท้ายรายการ · `app` = เว้นความสูง app bar ด้านบน (app bar z-index 5 ทับ v-main) -->
    <v-snackbar v-model="recordingQueuedSnackbar" :timeout="10000" color="#1e293b" top right app multi-line>
      <div class="d-flex align-start" data-testid="recording-queued-snackbar">
        <v-icon small color="#a3e635" class="mr-2 mt-1">mdi-video-outline</v-icon>
        <span>{{ $t('livefeature_recording_library_pending') }} {{ $t('livefeature_recording_check_report') }}</span>
      </div>
      <template #action="{ attrs }">
        <v-btn
          v-if="recordingQueuedSession"
          v-bind="attrs"
          small
          text
          color="#a3e635"
          class="text-capitalize"
          data-testid="recording-queued-report-btn"
          @click="openQueuedReport"
        >{{ $t('livefeature_report') }}</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import PermissionDialog from "@/components/PermissionDialog.vue";

// B03 (D27) — ฝาก session id ของ snackbar ข้ามการสร้าง component ใหม่
// (App.vue ใช้ router-view :key = $route.fullPath → replace ตัด query = ทำลาย instance นี้แล้วสร้างใหม่)
const RECORDING_QUEUED_STORAGE_KEY = "seed.live.recordingQueuedSessionId";

export default {
  name: "LiveIndex",

  components: {
    FullCalendar,
    PermissionDialog,
  },

  watch: {
    $route(to) {
      // B03 (D27) — recordingQueued ก่อน refresh · fetchSessions() ครั้งเดียว
      if (to.query.recordingQueued) {
        if (this.handoffRecordingQueued(to.query.recordingQueued)) return; // ไป fetch หลัง replace
        this.fetchSessions(); // fallback: sessionStorage ใช้ไม่ได้ → แสดงใน instance นี้แล้ว
        return;
      }
      // B03 — instance เดิมถูกใช้ต่อหลัง replace (ถ้า router-view เลิกใช้ key = fullPath) → รับค่าที่ฝากไว้
      if (this.restoreRecordingQueued() || to.query.refresh === "1") {
        this.fetchSessions();
      }
    },
  },

  async created() {
    if (!this.check_Permissions("live")) {
      this.$router.replace("/home/dashboard");
      return;
    }
    // B03 (D27) — มี recordingQueued: ฝาก id + replace แล้ว return (instance นี้ถูกทำลาย ไม่ fetch ซ้ำ)
    // instance ใหม่ (URL ไม่มี query): รับ id ที่ฝากไว้ → แสดง snackbar → fetch ครั้งเดียวตามปกติ
    if (this.$route.query.recordingQueued) {
      if (this.handoffRecordingQueued(this.$route.query.recordingQueued)) return;
    } else {
      this.restoreRecordingQueued();
    }
    await Promise.all([this.fetchSessions(), this.fetchTotalAttendees()]);
    this.clockTimer = window.setInterval(() => {
      this.currentTime = Date.now();
    }, 30000);
  },

  beforeDestroy() {
    if (this.clockTimer) {
      window.clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  },

  data() {
    const auth = JSON.parse(localStorage.getItem("vivek_authenticated_user_data") || "null");
    const localUserId = parseInt(localStorage.getItem("UserID"), 10);

    return {
      activeTab: 0,
      showCalendar: false,
      sessionsLoading: false,
      sessionsError: "",
      currentTime: Date.now(),
      clockTimer: null,
      deleteDialog: false,
      pendingDeleteSession: null,
      permissionDialog: false,
      permissionMessage: "",
      currentMemberId: auth?.memberId || "",
      currentAdminUserId: Number.isNaN(localUserId) ? 0 : localUserId,
      calendarOptions: {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: "dayGridMonth",
        events: [],
        buttonText: {
          today: this.$t("livefeature_today"),
        },
        headerToolbar: {
          start: "prev,today,next",
          center: "",
          end: "title",
        },
      },
      breadcrumbs: [
        {
          sidebar_tilte: "home",
        },
        {
          text: "live",
        },
      ],
      sessions: [],
      totalAttendeesMonthCount: 0,
      recordingQueuedSnackbar: false, // B03 (D27)
      recordingQueuedSessionId: "", // B03 (D27)
      recordingQueuedScrollPending: false, // B03 — เลื่อนบนสุดอีกครั้งหลังรายการโหลด (เฉพาะตอนแสดง snackbar)
    };
  },

  computed: {
    liveCount() {
      return this.sessions.filter((session) => session.status === "live").length;
    },

    scheduledCount() {
      return this.sessions.filter((session) => session.status === "scheduled").length;
    },

    recordingsCount() {
      return this.sessions.filter((session) => session.hasRecording).length;
    },

    totalAttendeesMonth() {
      return this.totalAttendeesMonthCount;
    },

    metrics() {
      return [
        {
          key: "live",
          label: this.$t("livefeature_live_now"),
          value: this.liveCount,
          valueClass: "metric-value--live",
        },
        {
          key: "scheduled",
          label: this.$t("livefeature_scheduled_this_week"),
          value: this.scheduledCount,
          valueClass: "metric-value--scheduled",
        },
        {
          key: "recordings",
          label: this.$t("livefeature_recordings_library"),
          value: this.recordingsCount,
          valueClass: "metric-value--recording",
        },
        {
          key: "attendees",
          label: this.$t("livefeature_total_attendees_month"),
          value: this.totalAttendeesMonth,
          valueClass: "metric-value--attendees",
        },
      ];
    },

    tabs() {
      return [
        { key: "all", label: this.$t("livefeature_all") },
        { key: "live", label: `${this.$t("livefeature_live_now")} (${this.liveCount})` },
        { key: "scheduled", label: `${this.$t("livefeature_scheduled")} (${this.scheduledCount})` },
        { key: "ended", label: this.$t("livefeature_ended") },
        { key: "recordings", label: this.$t("livefeature_recordings") },
      ];
    },

    filteredSessions() {
      const selectedTab = this.tabs[this.activeTab];

      if (!selectedTab || selectedTab.key === "all") {
        return this.decoratedSessions;
      }

      if (selectedTab.key === "recordings") {
        return this.decoratedSessions.filter((session) => session.hasRecording);
      }

      return this.decoratedSessions.filter(
        (session) => session.status === selectedTab.key
      );
    },

    decoratedSessions() {
      return this.sessions.map((session) => ({
        ...session,
        normalizedStatus: this.normalizeSessionStatus(session),
        canEnterHostRoom: this.canEnterHostRoom(session),
        isCurrentUserHostOrCoHost:
          this.currentAdminUserId > 0 &&
          (Number(session.hostUserID) === this.currentAdminUserId ||
            Number(session.coHostUserID) === this.currentAdminUserId),
        dateBadgeClass:
          this.normalizeSessionStatus(session) === "live"
            ? "session-card__date--live"
            : this.normalizeSessionStatus(session) === "scheduled"
            ? "session-card__date--scheduled"
            : this.normalizeSessionStatus(session) === "ended"
            ? "session-card__date--ended"
            : "session-card__date--recording",
        statusChipClass:
          this.normalizeSessionStatus(session) === "live"
            ? "session-status-chip--live"
            : this.normalizeSessionStatus(session) === "scheduled"
            ? "session-status-chip--scheduled"
            : this.normalizeSessionStatus(session) === "ended"
            ? "session-status-chip--ended"
            : "session-status-chip--recording",
        watchersText:
          session.watchers > 0
            ? `${session.watchers} ${this.$t("livefeature_watching")}`
            : this.$t("livefeature_audience_opens"),
          registrationText: `${this.$t("livefeature_registered_label")} ${session.registered}/${session.capacity}`,
      }));
    },

    // B03 (D27) — session ของ snackbar (หาไม่เจอ เช่นถูกลบ → snackbar ไม่มีปุ่มรายงาน)
    recordingQueuedSession() {
      return this.decoratedSessions.find((s) => String(s.id) === String(this.recordingQueuedSessionId)) || null;
    },

    deleteConfirmText() {
      if (!this.pendingDeleteSession) {
        return this.$t("suredelete");
      }

      return `${this.$t("livefeature_delete_live_session")} "${this.pendingDeleteSession.title}"?`;
    },
  },

  methods: {
    async fetchTotalAttendees() {
      const companyID = localStorage.getItem("companyID") || "";
      if (!companyID) return;
      try {
        const response = await this.$axios.get(
          `${this.web_url}LiveSession/GetTotalAttendeesCurrentMonth`,
          { params: { companyId: companyID }, headers: { accept: "*/*" } }
        );
        this.totalAttendeesMonthCount = response.data?.data ?? response.data ?? 0;
      } catch (_) {
        // Non-fatal — metric stays 0
      }
    },

    async fetchSessions() {
      this.sessionsLoading = true;
      this.sessionsError = "";
      const companyID = localStorage.getItem("companyID") || "";

      try {
        const response = await this.$axios.get(`${this.web_url}LiveSession/GetAllSessions`, {
          params: {
            companyId: companyID,
          },
          headers: {
            accept: "*/*",
          },
        });

        const apiSessions = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : [];

        this.sessions = apiSessions
          .filter((session) => session.activeFlag !== false && session.ActiveFlag !== false)
          .sort((leftSession, rightSession) => {
            const leftDate = new Date(
              leftSession.createdDate || leftSession.startTime || leftSession.scheduleDate || 0
            );
            const rightDate = new Date(
              rightSession.createdDate || rightSession.startTime || rightSession.scheduleDate || 0
            );

            const leftTime = Number.isNaN(leftDate.getTime()) ? 0 : leftDate.getTime();
            const rightTime = Number.isNaN(rightDate.getTime()) ? 0 : rightDate.getTime();

            return rightTime - leftTime;
          })
          .map((session) => this.mapApiSession(session));
        this.syncCalendarEvents();
      } catch (error) {
        this.sessions = [];
        this.sessionsError =
          error.response?.data?.message ||
          error.response?.data?.title ||
          this.$t("livefeature_failed_load_live_sessions");
      } finally {
        this.sessionsLoading = false;
        // B03 — รายการ render แล้วหน้ายาวขึ้น (scrollY เดิมจากหน้า Host อาจกลับมา) → เลื่อนบนสุดอีกครั้งให้ snackbar อยู่ในจอ
        if (this.recordingQueuedScrollPending) {
          this.recordingQueuedScrollPending = false;
          this.$nextTick(() => this.scrollTopForRecordingQueued());
        }
      }
    },

    mapApiSession(session) {
      const startTime = this.parseApiDate(session.startTime || session.scheduleDate);
      const endTime = this.parseApiDate(session.endTime || session.startTime);
      const normalizedStatus = this.normalizeApiStatus(session, startTime, endTime);
      const badgeDate = this.buildDateBadge(startTime);

      return {
        id: session.id,
        roomID: session.roomID,
        status: normalizedStatus,
        statusLabel: this.buildStatusLabel(normalizedStatus),
        dateLabel: badgeDate.dateLabel,
        dateNumber: badgeDate.dateNumber,
        time: this.formatTime(startTime),
        category: session.topicID ? `${this.$t("livefeature_topic_prefix")} #${session.topicID}` : this.$t("livefeature_live_session_prefix"),
        title: session.title || this.$t("livefeature_untitled_session"),
        host: this.buildHostLabel(session),
        watchers: normalizedStatus === "live" ? Number(session.noOfParticipants || 0) : 0,
        registered: Number(session.noOfParticipants || 0),
        capacity: Number(session.noOfParticipants || 0),
        duration: this.formatDuration(startTime, endTime),
        hasRecording: Boolean(session.autoRecord && normalizedStatus === "ended"),
        startTimeRaw: session.startTime,
        endTimeRaw: session.endTime,
        hostUserID: Number(session.hostUserID || session.HostUserID || 0),
        coHostUserID: Number(session.coHostUserID || session.CoHostUserID || 0),
      };
    },

    normalizeSessionStatus(session) {
      const rawStatus = String(session.status || session.statusLabel || "").trim().toLowerCase();

      if (rawStatus === "live now" || rawStatus === "live") {
        return "live";
      }

      if (rawStatus === "scheduled") {
        return "scheduled";
      }

      if (rawStatus === "ended") {
        return "ended";
      }

      return rawStatus;
    },

    normalizeApiStatus(session, startTime, endTime) {
      if (typeof session.status === "number") {
        if (session.status === 2) {
          return "live";
        }

        if (session.status === 3) {
          return "ended";
        }

        if (session.status === 0 || session.status === 1) {
          return "scheduled";
        }
      }

      const now = new Date();

      if (startTime && endTime && startTime <= now && endTime >= now) {
        return "live";
      }

      if (endTime && endTime < now) {
        return "ended";
      }

      return "scheduled";
    },

    parseApiDate(value) {
      if (!value) {
        return null;
      }

      const parsedDate = new Date(value);

      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return parsedDate;
    },

    buildDateBadge(dateValue) {
      if (!dateValue) {
        return {
          dateLabel: this.$t("livefeature_live"),
          dateNumber: "--",
        };
      }

      const today = new Date();
      const isToday =
        dateValue.getFullYear() === today.getFullYear() &&
        dateValue.getMonth() === today.getMonth() &&
        dateValue.getDate() === today.getDate();

      return {
        dateLabel: isToday
          ? this.$t("livefeature_today_badge")
          : dateValue.toLocaleString("en-US", { month: "short" }).toUpperCase(),
        dateNumber: String(dateValue.getDate()).padStart(2, "0"),
      };
    },

    formatTime(dateValue) {
      if (!dateValue) {
        return "--:--";
      }

      return dateValue.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatDuration(startTime, endTime) {
      if (!startTime || !endTime) {
        return this.$t("livefeature_session_time_pending");
      }

      const durationMinutes = Math.max(0, Math.round((endTime.getTime() - startTime.getTime()) / 60000));

      if (!durationMinutes) {
        return `0 ${this.$t("livefeature_min_session_suffix")}`;
      }

      return `${durationMinutes} ${this.$t("livefeature_min_session_suffix")}`;
    },

    buildHostScheduleSummary(session) {
      const startTime = this.parseApiDate(session.startTimeRaw);
      const endTime = this.parseApiDate(session.endTimeRaw);

      if (!startTime) {
        return session.duration || this.$t("livefeature_session_time_pending");
      }

      const dateLabel = startTime.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
      });
      const startLabel = startTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      const endLabel = endTime && !Number.isNaN(endTime.getTime())
        ? endTime.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
        : "";

      return endLabel ? `${dateLabel}, ${startLabel} - ${endLabel}` : `${dateLabel}, ${startLabel}`;
    },

    canEnterHostRoom(session) {
      const normalizedStatus = this.normalizeSessionStatus(session);

      if (normalizedStatus === "ended") {
        return false;
      }

      const startTime = this.parseApiDate(session.startTimeRaw);
      const endTime = this.parseApiDate(session.endTimeRaw);

      if (!startTime) {
        return false;
      }

      const tenMinutesBeforeStart = startTime.getTime() - 10 * 60 * 1000;

      if (this.currentTime < tenMinutesBeforeStart) {
        return false;
      }

      if (endTime && this.currentTime > endTime.getTime()) {
        return false;
      }

      return true;
    },

    buildHostLabel(session) {
      if (session.hostName) {
        return session.hostName;
      }

      if (session.hostUserID) {
        return `User #${session.hostUserID}`;
      }

      return this.$t("livefeature_host_pending");
    },

    buildStatusLabel(status) {
      if (status === "live") {
        return this.$t("livefeature_live_now");
      }

      if (status === "ended") {
        return this.$t("livefeature_ended");
      }

      return this.$t("livefeature_scheduled");
    },

    openHostRoom(session) {
      if (!session?.id) {
        return;
      }

      this.$router.push({
        path: `/home/live/host/${session.id}`,
        query: {
          title: session.title || this.$t("livefeature_untitled_session"),
          host: session.host || this.$t("livefeature_host_pending"),
          status: session.statusLabel || this.$t("livefeature_scheduled"),
          registered: String(session.registered || 0),
          dateLabel: session.dateLabel || this.$t("livefeature_live"),
          dateNumber: session.dateNumber || "--",
          time: session.time || "--:--",
          duration: this.buildHostScheduleSummary(session),
        },
      });
    },

    openReport(session) {
      if (!session?.id) {
        return;
      }

      this.$router.push({
        path: `/home/live/report/${session.id}`,
        query: {
          title: session.title || this.$t("livefeature_untitled_session"),
          host: session.host || this.$t("livefeature_host_pending"),
          status: session.statusLabel || this.$t("livefeature_ended"),
          registered: String(session.registered || 0),
          dateLabel: session.dateLabel || this.$t("livefeature_live"),
          dateNumber: session.dateNumber || "--",
          time: session.time || "--:--",
          duration: session.duration || this.$t("livefeature_session_time_pending"),
        },
      });
    },

    // B03 (D27) — แสดง snackbar (ปุ่มรายงานโผล่เมื่อรายการโหลดเสร็จ — computed recordingQueuedSession)
    showRecordingQueued(sessionId) {
      this.recordingQueuedSessionId = String(sessionId);
      this.recordingQueuedSnackbar = true;
      this.recordingQueuedScrollPending = true; // B03 — fetchSessions() เลื่อนซ้ำหลังรายการโหลด
      this.scrollTopForRecordingQueued();
    },

    // B03 — snackbar ยึดกล่อง v-main (App.vue .bg มี transform) → ตำแหน่งจริง = 72 − scrollY
    // ถ้าผู้สอน scroll หน้า Host ก่อนกด End หน้านี้จะได้ scrollY เดิม → snackbar อยู่เหนือจอ จึงเลื่อนบนสุด (เรียกเฉพาะเส้นทาง snackbar นี้)
    scrollTopForRecordingQueued() {
      try {
        window.scrollTo(0, 0);
      } catch (_) {
        // ไม่มี window.scrollTo (สภาพแวดล้อมพิเศษ) → ข้าม
      }
    },

    // B03 (D27) — ฝาก id ลง sessionStorage แล้ว replace ตัด query ทั้ง recordingQueued และ refresh
    // คืน true = ส่งต่อให้ instance ใหม่แล้ว (ผู้เรียกต้อง return ไม่ fetch) · storage ใช้ไม่ได้ → แสดงใน instance นี้ ไม่ replace → false
    handoffRecordingQueued(sessionId) {
      try {
        window.sessionStorage.setItem(RECORDING_QUEUED_STORAGE_KEY, String(sessionId));
      } catch (_) {
        this.showRecordingQueued(sessionId);
        return false;
      }
      this.$router.replace({ path: "/home/live" }).catch(() => {
        this.takeRecordingQueuedId(); // replace ถูกขัด (เช่นไปหน้าอื่น) → ไม่ให้ snackbar ค้างไปโผล่ครั้งหน้า
      });
      return true;
    },

    // B03 (D27) — อ่านแล้วลบทันที (reload ครั้งถัดไปไม่แสดงซ้ำ) · storage ใช้ไม่ได้ → ""
    takeRecordingQueuedId() {
      try {
        const sessionId = window.sessionStorage.getItem(RECORDING_QUEUED_STORAGE_KEY) || "";
        if (sessionId) window.sessionStorage.removeItem(RECORDING_QUEUED_STORAGE_KEY);
        return sessionId;
      } catch (_) {
        return "";
      }
    },

    // B03 (D27) — คืน true เมื่อมี id ที่ฝากไว้และแสดง snackbar แล้ว
    restoreRecordingQueued() {
      const sessionId = this.takeRecordingQueuedId();
      if (!sessionId) return false;
      this.showRecordingQueued(sessionId);
      return true;
    },

    openQueuedReport() {
      const session = this.recordingQueuedSession;
      this.recordingQueuedSnackbar = false;
      if (session) this.openReport(session);
    },

    openParticipantList(session) {
      this.$router.push({
        path: `/home/live/participant/${session.id}`,
        query: {
          title: session.title || this.$t("livefeature_untitled_session"),
          host: session.host || this.$t("livefeature_host_pending"),
          status: session.statusLabel || this.$t("livefeature_scheduled"),
          registered: String(session.registered || 0),
          dateLabel: session.dateLabel || this.$t("livefeature_live"),
          dateNumber: session.dateNumber || "--",
          time: session.time || "--:--",
          duration: session.duration || this.$t("livefeature_session_time_pending"),
        },
      });
    },

    openScheduleForm() {
      if (!this.check_Permissions("live")) {
        this.permissionMessage = "You don't have permission to add a Live Session.";
        this.permissionDialog = true;
        return;
      }
      this.$router.push("/home/live/schedule");
    },

    openEditForm(session) {
      if (!this.check_Permissions("live")) {
        this.permissionMessage = "You don't have permission to edit a Live Session.";
        this.permissionDialog = true;
        return;
      }
      this.$router.push({
        path: "/home/live/schedule",
        query: {
          mode: "edit",
          sessionId: String(session.id),
        },
      });
    },

    async deleteSession(session) {
      if (!this.check_Permissions("live")) {
        this.permissionMessage = "You don't have permission to delete a Live Session.";
        this.permissionDialog = true;
        return;
      }

      if (!session?.id) {
        return;
      }

      if (!this.currentMemberId) {
        this.sessionsError = this.$t("livefeature_unable_delete_missing_user");
        return;
      }

      this.pendingDeleteSession = session;
      this.deleteDialog = true;
    },

    closeDeleteDialog() {
      this.deleteDialog = false;
      this.pendingDeleteSession = null;
    },

    async confirmDeleteSession() {
      if (!this.pendingDeleteSession?.id) {
        this.closeDeleteDialog();
        return;
      }

      this.sessionsError = "";

      try {
        await this.$axios.delete(`${this.web_url}LiveSession/DeleteSession/${this.pendingDeleteSession.id}`, {
          params: {
            deletedBy: this.currentMemberId,
          },
          headers: {
            accept: "*/*",
          },
        });

        this.closeDeleteDialog();
        await this.fetchSessions();
      } catch (error) {
        this.sessionsError =
          error.response?.data?.message ||
          error.response?.data?.title ||
          this.$t("livefeature_failed_delete_live_session");
      }
    },

    toggleCalendar() {
      this.showCalendar = !this.showCalendar;
    },

    syncCalendarEvents() {
      this.calendarOptions.events = this.sessions.map((session) => ({
        id: String(session.id),
        title: session.title,
        date: this.buildCalendarDate(session),
        backgroundColor:
          session.status === "live"
            ? "#ef4444"
            : session.status === "scheduled"
            ? "#1976d2"
            : session.status === "ended"
            ? "#64748b"
            : "#06b6d4",
        borderColor:
          session.status === "live"
            ? "#ef4444"
            : session.status === "scheduled"
            ? "#1976d2"
            : session.status === "ended"
            ? "#64748b"
            : "#06b6d4",
      }));
    },

    buildCalendarDate(session) {
      if (session.startTimeRaw) {
        return String(session.startTimeRaw).split("T")[0];
      }

      const currentDate = new Date();
      return currentDate.toISOString().split("T")[0];
    },
  },
};
</script>

<style scoped>
.live-dashboard {
  background: linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%);
  min-height: 100%;
}

.calendar-panel {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.08);
}

.calendar-panel__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.calendar-panel__title {
  color: #0f172a;
  font-size: 1.25rem;
  font-weight: 700;
}

.calendar-panel__subtitle {
  color: #64748b;
  font-size: 0.95rem;
  margin-top: 6px;
}

.calendar-panel__sheet {
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 16px;
  overflow: hidden;
}

.metric-card {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
  padding: 22px 24px;
  height: 100%;
}

.metric-label {
  color: #64748b;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  margin-bottom: 12px;
}

.metric-value {
  font-size: 2.4rem;
  font-weight: 800;
  line-height: 1;
}

.metric-value--live {
  color: #dc2626;
}

.metric-value--scheduled {
  color: #1976d2;
}

.metric-value--recording {
  color: #059669;
}

.metric-value--attendees {
  color: #0f766e;
}

.live-tabs {
  border-bottom: 1px solid rgba(100, 116, 139, 0.18);
}

.session-card {
  display: flex;
  align-items: stretch;
  width: 100%;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.session-card__date {
  align-items: center;
  color: #0f172a;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-left: 4px solid transparent;
  min-width: 124px;
  padding: 20px 16px;
  text-align: center;
}

.session-card__date--live {
  background: #fff2f2;
  border-left-color: #ef4444;
  color: #dc2626;
}

.session-card__date--scheduled {
  background: #eff6ff;
  border-left-color: #1976d2;
  color: #1976d2;
}

.session-card__date--ended {
  background: #f8fafc;
  border-left-color: #64748b;
  color: #475569;
}

.session-card__date--recording {
  background: #f0fdfa;
  border-left-color: #06b6d4;
  color: #0f766e;
}

.session-status-chip {
  border: 1px solid transparent;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  box-shadow: none;
  font-size: 0.74rem;
  font-weight: 500;
  letter-spacing: 0.06em;
}

.session-status-chip__icon {
  font-size: 0.9rem !important;
}

.session-status-chip--live {
  background: #fee2e2 !important;
  border-color: #fecaca;
  color: #dc2626 !important;
}

.session-status-chip--scheduled {
  background: #e3f2fd !important;
  border-color: #bbdefb;
  color: #1976d2 !important;
}

.session-status-chip--ended {
  background: #e2e8f0 !important;
  border-color: #cbd5e1;
  color: #475569 !important;
}

.session-status-chip--recording {
  background: #ccfbf1 !important;
  border-color: #99f6e4;
  color: #0f766e !important;
}

.session-card__day {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  margin-bottom: 10px;
}

.session-card__date-number {
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 10px;
}

.session-card__time {
  font-size: 1rem;
  font-weight: 600;
}

.session-card__body {
  flex: 1;
  min-width: 0;
  padding: 22px 24px;
}

.session-card__topline {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.session-card__category {
  color: #475569;
  font-size: 0.95rem;
  font-weight: 400;
}

.session-card__title {
  color: #0f172a;
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.3;
}

.session-card__host {
  color: #334155;
  font-size: 0.98rem;
  font-weight: 400;
}

.session-card__meta {
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  font-size: 0.95rem;
  font-weight: 400;
}

.session-card__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  min-width: 220px;
  padding: 22px 24px;
}

.session-action-btn {
  min-width: 160px;
  border-width: 1px;
  box-shadow: none;
  letter-spacing: 0;
  text-transform: none;
}

.empty-state {
  background: rgba(255, 255, 255, 0.84);
  border: 1px dashed rgba(100, 116, 139, 0.3);
}

.empty-state__title {
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.empty-state__subtitle {
  color: #64748b;
}

::v-deep .fc {
  background: #ffffff;
}

::v-deep .fc .fc-button-primary {
  background-color: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  color: #334155 !important;
  text-transform: capitalize;
}

::v-deep .fc .fc-button-primary:not(:disabled).fc-button-active,
::v-deep .fc .fc-button-primary:not(:disabled):active {
  background-color: #ecfdf5 !important;
  border-color: #10b981 !important;
  color: #047857 !important;
}

::v-deep .fc .fc-toolbar-title {
  color: #0f172a;
  font-size: 1.1rem !important;
  font-weight: 700;
}

::v-deep .fc-col-header {
  background-color: #f8fafc !important;
}

::v-deep .fc-col-header-cell-cushion {
  color: #475569 !important;
  font-size: 0.88rem !important;
  text-transform: uppercase;
}

::v-deep .fc-daygrid-day-number {
  color: #475569 !important;
}

::v-deep .fc-h-event {
  border-width: 0 !important;
  border-radius: 8px !important;
}

::v-deep .fc-event-title {
  font-size: 0.8rem !important;
  font-weight: 500;
}

@media (max-width: 960px) {
  .session-card {
    flex-direction: column;
  }

  .session-card__date {
    min-width: 100%;
    padding: 18px;
  }

  .session-card__actions {
    justify-content: flex-start;
    min-width: 100%;
    padding-top: 0;
  }
}

@media (max-width: 600px) {
  .live-dashboard {
    padding-left: 16px;
    padding-right: 16px;
  }

  .metric-value {
    font-size: 2rem;
  }

  .session-card__body,
  .session-card__actions {
    padding-left: 18px;
    padding-right: 18px;
  }

  .session-card__title {
    font-size: 1.08rem;
  }
}
</style>
